/**
 * The generic vocabulary for a **patient-scoped token**: a bearer credential a staff member issues
 * for exactly one patient (or one visit), that authenticates *no* staff identity and reaches *no*
 * resource beyond that one patient's own record. `BACKLOG.md` C7 (kiosk check-in) is the first
 * consumer; `C19` (driver's-license scan) and `C21` (SMS patient-experience survey) are named
 * follow-ups that reuse this same mechanism rather than inventing their own — see
 * `server/src/auth/patientScopedTokens.ts` for the crypto/validity half (CSPRNG generation,
 * sha256 storage, expiry/revocation), which follows `verificationTokens.ts`'s established pattern.
 *
 * This is a third auth surface, distinct from both `AUTH_PUBLIC_PATHS`' staff-session cookie
 * (`auth/middleware.ts`'s `requireSession`) and the ordinary fully-public unauthenticated paths —
 * `emr-po` A16's identity-surface question, answered 2026-08-18, confirmed 2026-08-24.
 *
 * Deliberately kept purpose-generic: a new consumer adds a value to `PATIENT_SCOPED_TOKEN_PURPOSES`
 * and its own route/service module (`server/src/kiosk/` is the kiosk instance), not a new table, a
 * new token module, or a new crypto scheme.
 */

import { z } from 'zod';

/**
 * `kiosk_checkin`, `license_scan`, `patient_survey`, and `pre_visit_intake` are the four purposes this
 * build issues today. Each purpose gets its own TTL policy and scope shape at the call site that
 * issues it (`server/src/kiosk/service.ts`'s `KIOSK_CHECKIN_TOKEN_TTL_MS`;
 * `server/src/licenseScan/service.ts`'s `LICENSE_SCAN_TOKEN_TTL_MS`;
 * `server/src/patientSurvey/service.ts`'s `PATIENT_SURVEY_TOKEN_TTL_MS`;
 * `server/src/patientIntake/service.ts`'s `PATIENT_INTAKE_TOKEN_TTL_MS`), not a shared default: a
 * 3-5 minute kiosk window and a multi-day pre-visit-intake link are genuinely different policies for
 * the same mechanism, per `emr-po` A18's own note (about the sibling `patient_survey` purpose) that a
 * non-kiosk link "needs a longer, non-kiosk TTL." `license_scan` (`BACKLOG.md` C19,
 * `docs/decisions/patient-license-scan-scope.md` §4), `patient_survey` (`BACKLOG.md` C21), and
 * `pre_visit_intake` (`BACKLOG.md` C17's pre-visit-history-intake slice) are each their own module
 * (`server/src/licenseScan/`, `server/src/patientSurvey/`, `server/src/patientIntake/`), not a
 * parameterized version of `kiosk/` — that decision doc's own §4 states this explicitly:
 * `requireKioskToken` is hardcoded to `purpose: 'kiosk_checkin'`, so a new purpose needs a sibling
 * middleware/service/route module, matching this file's own "each purpose gets its own... route/
 * service module" header line above.
 */
export const PATIENT_SCOPED_TOKEN_PURPOSES = [
  'kiosk_checkin',
  'license_scan',
  'patient_survey',
  'pre_visit_intake',
] as const;
export type PatientScopedTokenPurpose = (typeof PATIENT_SCOPED_TOKEN_PURPOSES)[number];
export const patientScopedTokenPurposeSchema = z.enum(PATIENT_SCOPED_TOKEN_PURPOSES);
